// BOT-EDIT: 2025-01-27T10:50:00Z Create invoiceGenerator utility for web version
import jsPDF from 'jspdf'; // Still needed for type definition if we want to keep pdfToBlob signature, but not for generation
import html2pdf from 'html2pdf.js'; // Import html2pdf
import i18next from 'i18next';

export interface InvoiceItem {
  id: number;
  description: string;
  quantity: number;
  price: number;
}

export interface Invoice {
  invoiceNumber: string;
  issueDate: Date;
  dueDate: Date;
  customerName: string;
  customerCompany: string;
  customerEmail: string;
  customerPhone: string;
  customerAddress: string;
  items: InvoiceItem[];
  taxRate: number;
  notes: string;
  documentType: string;
  documentTitle: string;
}

export interface BusinessProfile {
  businessName: string;
  businessType?: string;
  businessAddress?: string;
  businessPhone?: string;
  businessEmail?: string;
  address?: string;
  phone?: string;
  email?: string;
  website?: string;
  abn?: string;
  bankName?: string;
  bankAccountNumber?: string;
  bankAccountHolder?: string;
  logoUrl?: string;
}

// 인보이스 번호 생성
export function generateInvoiceNumber(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
  return `INV-${year}${month}${day}-${random}`;
}

// 날짜 포맷팅 함수 (앱과 동일)
const formatDate = (date: Date) => {
  const d = date instanceof Date ? date : new Date(date);
  return `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getDate().toString().padStart(2, '0')}`;
};

// 숫자를 금액 형식으로 포맷팅하는 유틸리티 함수 (앱과 동일)
const formatCurrency = (amount: number, langCode: string = 'en') => {
  if (typeof amount !== 'number') {
    return '$0';
  }
  
  const currencySymbol = '$';
  
  // 소수점 둘째 자리까지 반올림
  const roundedAmount = Math.round(amount * 100) / 100;
  
  // 언어별 숫자 포맷팅
  let formattedAmount;
  if (langCode === 'ko') {
    formattedAmount = roundedAmount.toLocaleString('ko-KR');
  } else if (langCode === 'en') {
    formattedAmount = roundedAmount.toLocaleString('en-US');
  } else {
    formattedAmount = roundedAmount.toLocaleString();
  }
  
  // 통화 기호를 항상 앞에 배치
  return `${currencySymbol}${formattedAmount}`;
};

// 번역 함수 (앱과 동일한 키 사용)
const translate = (key: string, language: string = 'en') => {
  try {
    // i18next가 초기화되어 있으면 사용, 아니면 기본값 사용
    if (i18next.isInitialized) {
      return i18next.t(key, { lng: language }) || key;
    } else {
      // 앱과 동일한 번역 매핑
      const translations: { [key: string]: { [lang: string]: string } } = {
        // invoice 관련 (앱과 동일)
        'invoice.title': { en: 'Invoice', ko: '인보이스' },
        'invoice.billTo': { en: 'Bill To', ko: '청구 대상' },
        'invoice.thankYou': { en: 'Thank you for your business!', ko: '저희 비즈니스를 이용해주셔서 감사합니다!' },
        'invoice.generatedOn': { en: 'Generated on', ko: '생성일' },
        'invoice.reference': { en: 'Reference', ko: '참조' },
        'invoice.paymentInfo': { en: 'Payment Information', ko: '결제 정보' },
        
        // businessProfile 관련 (앱과 동일)
        'businessProfile.phone': { en: 'Phone', ko: '전화번호' },
        'businessProfile.email': { en: 'Email', ko: '이메일' },
        'businessProfile.website': { en: 'Website', ko: '웹사이트' },
        'businessProfile.abn': { en: 'ABN', ko: '사업자 등록번호' },
        'businessProfile.bankName': { en: 'Bank', ko: '은행명' },
        'businessProfile.bankAccountHolder': { en: 'Account Holder', ko: '예금주' },
        'businessProfile.bankAccountNumber': { en: 'Account Number', ko: '계좌번호' },
        
        // invoiceCreation 관련 (앱과 동일)
        'invoiceCreation.itemDescription': { en: 'Description', ko: '항목 설명' },
        'invoiceCreation.quantity': { en: 'Qty', ko: '수량' },
        'invoiceCreation.unitPrice': { en: 'Price', ko: '단가' },
        'invoiceCreation.amount': { en: 'Total', ko: '금액' },
        'invoiceCreation.subtotal': { en: 'Subtotal', ko: '소계' },
        'invoiceCreation.tax': { en: 'Tax', ko: '세금' },
        'invoiceCreation.total': { en: 'Total', ko: '합계' },
        'invoiceCreation.notes': { en: 'Notes', ko: '메모' },
        'invoiceCreation.invoiceNumber': { en: 'Invoice Number', ko: '인보이스 번호' },
        'invoiceCreation.issueDate': { en: 'Issue Date', ko: '발행일' },
        'invoiceCreation.dueDate': { en: 'Due Date', ko: '결제 기한' },
        'invoiceCreation.documentTypeInvoice': { en: 'INVOICE', ko: '인보이스' }
      };
      
      return translations[key]?.[language] || translations[key]?.['en'] || key;
    }
  } catch {
    return key;
  }
};

// 앱과 동일한 HTML 기반 인보이스 생성
export async function generateSampleInvoice(
  invoice: Invoice,
  businessProfile: BusinessProfile,
  language: string = 'en'
): Promise<Blob> { // Change return type to Promise<Blob>
  
  console.log('🔍 generateSampleInvoice - businessProfile:', businessProfile);
  console.log('🔍 generateSampleInvoice - language:', language);
  
  // 비즈니스 정보 추출 (앱과 동일한 방식)
  const companyName = businessProfile.businessName || '';
  const companyAddress = businessProfile.businessAddress || businessProfile.address || '';
  const companyPhone = businessProfile.businessPhone || businessProfile.phone || '';
  const companyEmail = businessProfile.businessEmail || businessProfile.email || '';
  const companyWebsite = businessProfile.website || '';
  const logoUrl = businessProfile.logoUrl || '';
  const companyAbn = businessProfile.businessType || businessProfile.abn || '';
  
  console.log('🔍 generateSampleInvoice - logoUrl:', logoUrl);
  
  // 아이템 계산
  const items = invoice.items?.length > 0 ? 
                invoice.items.map(item => ({...item, amount: (item.quantity || 0) * (item.price || 0)})) :
                [
                  { description: translate('invoiceCreation.itemDescription', language), quantity: 1, price: 0, amount: 0 }
                ];

  // 금액 계산
  const subtotal = items.reduce((sum, item) => sum + (item.amount || 0), 0);
  const taxAmount = subtotal * (invoice.taxRate / 100);
  const total = subtotal + taxAmount;

  // 고객 정보
  const customerInfo = {
    name: invoice.customerName || 'N/A',
    address: invoice.customerAddress || 'N/A',
    company: invoice.customerCompany || '',
    email: invoice.customerEmail || '',
    phone: invoice.customerPhone || ''
  };
  
  // 결제 정보
  const paymentInfo = {
    bankName: businessProfile.bankName || '',
    accountNo: businessProfile.bankAccountNumber || '',
    accountHolder: businessProfile.bankAccountHolder || ''
  };

  // 앱과 동일한 HTML 템플릿 사용
  const htmlContent = `
    <!DOCTYPE html>
    <html lang="${language}">
    <head>
      <meta charset="utf-8">
      <title>${translate('invoice.title', language)} ${invoice.invoiceNumber}</title>
      <style>
        @import url('https://fonts.googleapis.com/css2?family=Noto+Sans+KR:wght@400;700&display=swap');
                         body { 
          font-family: 'Noto Sans KR', 'Arial', sans-serif; 
          font-size: 12px; 
          margin: 25px; 
          padding-bottom: 20px; /* 하단 여백 줄임 */
          color: #333; 
        }
        .header { border-bottom: 1px solid #ddd; padding-bottom: 15px; margin-bottom: 20px; }
        .company-name { font-size: 18px; font-weight: bold; color: #2a5885; margin-bottom: 8px; }
        .invoice-info { text-align: right; margin-top: 10px; }
        .document-title { 
          font-size: 24px; 
          font-weight: bold; 
          color: #2a5885; 
          text-align: right;
          margin-bottom: 15px;
          text-transform: uppercase;
          letter-spacing: 1px;
        }
        .section-title { font-weight: bold; color: #2a5885; margin-bottom: 8px; }
        .items-table { width: 100%; border-collapse: collapse; margin: 20px 0; }
        .items-table th, .items-table td { border: 1px solid #ddd; padding: 8px; }
        .items-table th { background-color: #f5f5f5; font-weight: bold; }
        .text-right { text-align: right; }
        .totals-table { width: 300px; margin-left: auto; margin-bottom: 20px; }
        .totals-table td { padding: 5px; border-bottom: 1px solid #ddd; }
        .total-row { font-weight: bold; border-top: 2px solid #333; }
                 .footer { margin-top: 30px; padding-bottom: 10px; text-align: center; font-size: 11px; color: #666; }
        .logo-container { margin-bottom: 10px; }
        .logo-image { max-width: 120px; max-height: 120px; display: block; }
      </style>
    </head>
    <body>
      <!-- Header -->
      <div class="header">
        <table width="100%">
          <tr>
            <td width="60%">
              ${logoUrl ? `
                <div class="logo-container">
                  <img src="${logoUrl}" class="logo-image" alt="Company Logo" />
                </div>
              ` : ''}
              ${companyName ? `<div class="company-name">${companyName}</div>` : ''}
              ${companyAddress ? `<div>${companyAddress}</div>` : ''}
              ${companyPhone ? `<div>${translate('businessProfile.phone', language)}: ${companyPhone}</div>` : ''}
              ${companyEmail ? `<div>${translate('businessProfile.email', language)}: ${companyEmail}</div>` : ''}
              ${companyWebsite ? `<div>${translate('businessProfile.website', language)}: ${companyWebsite}</div>` : ''}
              ${companyAbn ? `<div>${translate('businessProfile.abn', language)}: ${companyAbn}</div>` : ''}
            </td>
            <td width="40%">
              <!-- Document Title -->
              <div class="document-title">${invoice.documentTitle || translate('invoiceCreation.documentTypeInvoice', language)}</div>
              <div class="invoice-info">
                <div><strong>${translate('invoiceCreation.invoiceNumber', language)}:</strong> ${invoice.invoiceNumber}</div>
                <div><strong>${translate('invoiceCreation.issueDate', language)}:</strong> ${formatDate(invoice.issueDate)}</div>
                <div><strong>${translate('invoiceCreation.dueDate', language)}:</strong> ${formatDate(invoice.dueDate)}</div>
              </div>
            </td>
          </tr>
        </table>
      </div>

      <!-- Bill To -->
      <div style="margin-bottom: 20px;">
        <div class="section-title">${translate('invoice.billTo', language)}:</div>
        <div><strong>${customerInfo.name}</strong></div>
        ${customerInfo.company ? `<div>${customerInfo.company}</div>` : ''}
        <div>${customerInfo.address}</div>
        ${customerInfo.email ? `<div>${translate('businessProfile.email', language)}: ${customerInfo.email}</div>` : ''}
        ${customerInfo.phone ? `<div>${translate('businessProfile.phone', language)}: ${customerInfo.phone}</div>` : ''}
      </div>

      <!-- Items -->
      <table class="items-table">
        <thead>
          <tr>
            <th width="50%">${translate('invoiceCreation.itemDescription', language)}</th>
            <th width="15%" class="text-right">${translate('invoiceCreation.quantity', language)}</th>
            <th width="15%" class="text-right">${translate('invoiceCreation.unitPrice', language)}</th>
            <th width="20%" class="text-right">${translate('invoiceCreation.amount', language)}</th>
          </tr>
        </thead>
        <tbody>
          ${items.map(item => `
            <tr>
              <td>${item.description}</td>
              <td class="text-right">${item.quantity}</td>
              <td class="text-right">${formatCurrency(item.price, language)}</td>
              <td class="text-right">${formatCurrency(item.amount, language)}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>

      <!-- Totals -->
      <table class="totals-table">
        <tr>
          <td>${translate('invoiceCreation.subtotal', language)}:</td>
          <td class="text-right">${formatCurrency(subtotal, language)}</td>
        </tr>
        <tr>
          <td>${translate('invoiceCreation.tax', language)} (${invoice.taxRate}%):</td>
          <td class="text-right">${formatCurrency(taxAmount, language)}</td>
        </tr>
        <tr class="total-row">
          <td><strong>${translate('invoiceCreation.total', language)}:</strong></td>
          <td class="text-right"><strong>${formatCurrency(total, language)}</strong></td>
        </tr>
      </table>

      <!-- Payment Information -->
      ${(paymentInfo.bankName || paymentInfo.accountNo || paymentInfo.accountHolder) ? `
      <div style="margin: 20px 0; padding: 10px; background-color: #f5f5f5;">
        <div class="section-title">${translate('invoice.paymentInfo', language)}:</div>
        ${paymentInfo.bankName ? `<div>${translate('businessProfile.bankName', language)}: ${paymentInfo.bankName}</div>` : ''}
        ${paymentInfo.accountHolder ? `<div>${translate('businessProfile.bankAccountHolder', language)}: ${paymentInfo.accountHolder}</div>` : ''}
        ${paymentInfo.accountNo ? `<div>${translate('businessProfile.bankAccountNumber', language)}: ${paymentInfo.accountNo}</div>` : ''}
      </div>
      ` : ''}

      ${invoice.notes ? `
        <div style="margin: 20px 0; padding: 10px; background-color: #f5f5f5;">
          <div class="section-title">${translate('invoiceCreation.notes', language)}:</div>
          <div>${invoice.notes}</div>
        </div>
      ` : ''}

      <!-- Footer -->
      <div class="footer">
        <p><strong>${translate('invoice.thankYou', language)}</strong></p>
        <p>${translate('invoice.generatedOn', language)} ${new Date().toLocaleDateString(language)} | ${translate('invoice.reference', language)}: ${invoice.invoiceNumber}</p>
      </div>
    </body>
    </html>
  `;

  console.log('🔍 generateSampleInvoice - HTML content generated');

  const element = document.createElement('div');
  element.innerHTML = htmlContent;

  const opt = {
    margin: [25, 25, 20, 25], // [top, right, bottom, left] - 하단 여백 줄임
    filename: `invoice_${invoice.invoiceNumber}.pdf`,
    image: { type: 'jpeg', quality: 0.98 },
    html2canvas: { 
      scale: 1.5, // scale을 낮춰서 더 안정적으로
      useCORS: true,
      allowTaint: true
    },
    jsPDF: { 
      unit: 'mm', 
      format: 'a4', 
      orientation: 'portrait',
      compress: true
    }
  };

  const pdfBlob = await html2pdf().from(element).set(opt).output('blob');
  
  console.log('🔍 generateSampleInvoice - PDF blob generated');
  return pdfBlob;
}

export function pdfToBlob(pdfBlob: Blob): Promise<Blob> { // Changed input type
  return Promise.resolve(pdfBlob);
}

// PDF를 URL로 변환
export function pdfToUrl(pdfBlob: Blob): string {
  return URL.createObjectURL(pdfBlob);
}

